import {
    Checkbox,
    InputField,
    RadioButtons,
    RadioButtonType
} from '@swegon-core/uic';
import { Info } from '@swegon-core/uic/dist/src/theme';
import { SelectFieldOption } from '@swegon-core/uic/dist/src/components/forms/types';
import { useContext, useState } from 'react';
import { InputFields } from './Home';
import {
    AirDataContainer,
    EmptyGridRow,
    IconContainer,
    InputFieldContainer,
    InputFieldLabel
} from './InputFieldsContainer.styles';
import { Context as TranslationContext } from '../shared/contexts/TranslationContext';
import { Damper } from '../types/AirHandlingUnitLite.types';

export interface InputFieldsProps {
    inputFields: InputFields;
    selectedCountryAndCity: SelectedCountryAndCity;
    climateLocations: ClimateLocationsDto | undefined;
    onSelectedCountryOrCityChanged: (value: SelectedCountryAndCity) => void;
    airflowBounds?: AirflowBounds;
    onInputChange: (values: InputFields, inputError: boolean) => void;
    coilAndDamperOptions: CoilAndDamperOptions;
    supplyDisabled: boolean;
    extractDisabled: boolean;
}

const COIL_OPTIONS = ['heating', 'cooling', 'none'] as const;
export type Coil = (typeof COIL_OPTIONS)[number];

export type CoilAndDamperOptions = {
    coils: boolean;
    dampers: Damper[];
};

export type AirflowBounds = {
    supplyAirflowMin?: number;
    supplyAirflowMax?: number;
    extractAirflowMin?: number;
    extractAirflowMax?: number;
};

export type CityDto = {
    key: number;
    name: string;
};

export type CountryDto = {
    key: number;
    name: string;
    cities: CityDto[];
    defaultCity: CityDto;
};
export type ClimateLocationsDto = {
    countries: CountryDto[];
    defaultCountry: CountryDto;
};
export type City = {
    value: number;
    name: string;
};

export type Country = {
    value: number;
    name: string;
};

export type SelectedCountryAndCity = {
    selectedCountryKey: number;
    selectedCityKey: number;
};

const getCitiesForCountry = (
    climateLocations: ClimateLocationsDto,
    countryKey: number
): SelectFieldOption[] => {
    const countryToSet = climateLocations.countries.find(
        (country) => country.key === countryKey
    );
    return (
        countryToSet?.cities.map(
            (city) =>
                ({
                    value: city.key,
                    title: city.name
                }) as SelectFieldOption
        ) ?? []
    );
};

const getDefaultCityForCountry = (
    climateLocations: ClimateLocationsDto,
    countryKey: number
): number =>
    climateLocations.countries.find((country) => country.key === countryKey)
        ?.defaultCity.key ?? 0;

const InputFieldsContainer = ({
    inputFields,
    onSelectedCountryOrCityChanged,
    climateLocations,
    selectedCountryAndCity,
    airflowBounds,
    onInputChange,
    coilAndDamperOptions,
    supplyDisabled,
    extractDisabled
}: InputFieldsProps) => {
    const { l } = useContext(TranslationContext);
    const [inputErrors, setInputErrors] = useState(false);

    const onCountryChanged = (countryKey: number) => {
        if (!climateLocations) {
            return;
        }
        const defaultCity = getDefaultCityForCountry(
            climateLocations,
            countryKey
        );
        onSelectedCountryOrCityChanged({
            selectedCityKey: defaultCity,
            selectedCountryKey: countryKey
        });
    };

    const onCityChanged = (cityKey: number) => {
        onSelectedCountryOrCityChanged({
            selectedCityKey: cityKey,
            selectedCountryKey: selectedCountryAndCity.selectedCountryKey
        });
    };

    const onInputFieldChangeHandler = (
        fieldId: keyof InputFields,
        value: number | string
    ) => {
        if (Object.keys(inputFields).includes(fieldId as keyof InputFields)) {
            let hasInputErrors = inputErrors;
            if (
                typeof value === 'number' &&
                (fieldId === 'supplyAirflow' || fieldId === 'extractAirflow')
            ) {
                const airflowNotInRange = airflowNotWithinBounds(
                    fieldId,
                    value
                );
                if (airflowNotInRange) {
                    hasInputErrors = true;
                } else if (inputErrors) {
                    hasInputErrors = false;
                }
            }

            onInputChange(
                {
                    ...inputFields,
                    [fieldId]: value
                },
                hasInputErrors
            );
            setInputErrors(hasInputErrors);
        }
    };

    const onDampersChangedHandler = (checked: boolean, value: Damper) => {
        let newDampers = inputFields.dampers;

        if (checked) {
            newDampers = newDampers?.concat([value]) ?? [value];
        } else {
            newDampers = newDampers?.filter((d) => d !== value);
        }
        onInputChange(
            {
                ...inputFields,
                dampers: newDampers
            },
            inputErrors
        );
    };

    const airflowNotWithinBounds = (
        fieldId: 'supplyAirflow' | 'extractAirflow',
        value: number
    ) => {
        if (
            fieldId === 'supplyAirflow' &&
            airflowBounds?.supplyAirflowMin !== undefined &&
            airflowBounds?.supplyAirflowMax !== undefined
        ) {
            return (
                value < airflowBounds.supplyAirflowMin ||
                value > airflowBounds.supplyAirflowMax
            );
        } else if (
            fieldId === 'extractAirflow' &&
            airflowBounds?.extractAirflowMin !== undefined &&
            airflowBounds?.extractAirflowMax !== undefined
        ) {
            return (
                value < airflowBounds.extractAirflowMin ||
                value > airflowBounds.extractAirflowMax
            );
        }
        return false;
    };

    const airflowStep =
        inputFields.airflowUnit.key === 'm3_h'
            ? 1
            : inputFields.airflowUnit.key === 'm3_s'
              ? 0.01
              : inputFields.airflowUnit.key === 'l_s'
                ? 1
                : 10;

    const pressureDropStep = inputFields.pressureUnit.key === 'Incw' ? 0.1 : 50;

    const countries =
        climateLocations?.countries.map((country) => ({
            value: country.key,
            title: country.name
        })) ?? [];

    const citiesToShow = climateLocations
        ? getCitiesForCountry(
              climateLocations,
              selectedCountryAndCity.selectedCountryKey
          )
        : [];

    const coilTranslationMap: { [key in Coil]: string } = {
        cooling: 'ahuDesignLite.coil.supply.coolingWater',
        heating: 'ahuDesignLite.coil.supply.heatingWater',
        none: 'ahuDesignLite.coil.none'
    };
    const damperTranslationMap: { [key in Damper]: string } = {
        exhaust: 'ahuDesignLite.damper.exhaust',
        outdoor: 'ahuDesignLite.damper.outdoor'
    };
    const coilsToShow: RadioButtonType[] =
        COIL_OPTIONS?.map((c) => ({
            label: l(coilTranslationMap[c]),
            value: String(c)
        })) ?? [];

    return (
        <div>
            <AirDataContainer>
                <InputFieldLabel
                    $gridrow={1}
                    $gridcolumn={2}
                    id="supply-air-label"
                >
                    {l('common.supplyAir')}
                </InputFieldLabel>
                <InputFieldLabel
                    $gridrow={1}
                    $gridcolumn={3}
                    id="extract-air-label"
                >
                    {l('common.extractAir')}
                </InputFieldLabel>
                <InputFieldLabel
                    $gridrow={2}
                    $gridcolumn={1}
                    id="airflow-label"
                >
                    {l('common.airFlow')}
                </InputFieldLabel>
                <InputFieldContainer $gridrow={2} $gridcolumn={2}>
                    <InputField
                        id="supplyAirflow"
                        aria-labelledby="airflow-label supply-air-label"
                        type="number"
                        value={inputFields?.supplyAirflow}
                        unit={inputFields.airflowUnit.translated}
                        onChange={(val) =>
                            onInputFieldChangeHandler('supplyAirflow', val)
                        }
                        step={airflowStep}
                        errorText={
                            airflowNotWithinBounds(
                                'supplyAirflow',
                                inputFields?.supplyAirflow
                            )
                                ? l('ahuDesignLite.airflowNotInRange')
                                : undefined
                        }
                        placeholder="1"
                        disabled={supplyDisabled}
                    />
                </InputFieldContainer>
                <InputFieldContainer $gridrow={2} $gridcolumn={3}>
                    <InputField
                        id="extractAirflow"
                        aria-labelledby="airflow-label extract-air-label"
                        type="number"
                        unit={inputFields.airflowUnit.translated}
                        value={inputFields?.extractAirflow}
                        onChange={(val) =>
                            onInputFieldChangeHandler('extractAirflow', val)
                        }
                        step={airflowStep}
                        errorText={
                            airflowNotWithinBounds(
                                'extractAirflow',
                                inputFields?.extractAirflow
                            )
                                ? l('ahuDesignLite.airflowNotInRange')
                                : undefined
                        }
                        placeholder="1"
                        disabled={extractDisabled}
                    />
                </InputFieldContainer>
                <InputFieldLabel
                    $gridrow={3}
                    $gridcolumn={1}
                    id="pressure-drop-inside-label"
                >
                    {l('common.pressureDrop')}
                </InputFieldLabel>
                <InputFieldContainer $gridrow={3} $gridcolumn={2}>
                    <InputField
                        id="pressureDropSupply"
                        aria-labelledby="pressure-drop-inside-label supply-air-label"
                        type="number"
                        unit={inputFields.pressureUnit.translated}
                        value={inputFields?.pressureDropSupply}
                        onChange={(val) =>
                            onInputFieldChangeHandler('pressureDropSupply', val)
                        }
                        step={pressureDropStep}
                        disabled={supplyDisabled}
                    />
                </InputFieldContainer>
                <InputFieldContainer $gridrow={3} $gridcolumn={3}>
                    <InputField
                        id="pressureDropExtract"
                        aria-labelledby="pressure-drop-inside-label extract-air-label"
                        type="number"
                        unit={inputFields.pressureUnit.translated}
                        value={inputFields?.pressureDropExtract}
                        onChange={(val) =>
                            onInputFieldChangeHandler(
                                'pressureDropExtract',
                                val
                            )
                        }
                        step={pressureDropStep}
                        disabled={extractDisabled}
                    />
                </InputFieldContainer>
                <EmptyGridRow $gridrow={4} $gridcolumn={1} />
                <InputFieldLabel
                    $gridrow={5}
                    $gridcolumn={2}
                    id="outdoor-label"
                >
                    {l('common.outdoorAir')}
                </InputFieldLabel>
                <InputFieldLabel
                    $gridrow={5}
                    $gridcolumn={3}
                    id="exhaust-label"
                >
                    {l('common.exhaustAir')}
                </InputFieldLabel>
                <InputFieldLabel
                    $gridrow={6}
                    $gridcolumn={1}
                    id="pressure-drop-outside-label"
                >
                    {l('common.pressureDrop')}
                </InputFieldLabel>
                <InputFieldContainer $gridrow={6} $gridcolumn={2}>
                    <InputField
                        id="pressureDropOutdoor"
                        aria-labelledby="pressure-drop-outside-label outdoor-label "
                        type="number"
                        unit={inputFields.pressureUnit.translated}
                        value={inputFields?.pressureDropOutdoor}
                        onChange={(val) =>
                            onInputFieldChangeHandler(
                                'pressureDropOutdoor',
                                val
                            )
                        }
                        step={pressureDropStep}
                        disabled={supplyDisabled}
                    />
                </InputFieldContainer>
                <InputFieldContainer $gridrow={6} $gridcolumn={3}>
                    <InputField
                        id="pressureDropExhaust"
                        aria-labelledby="pressure-drop-outside-label exhaust-label "
                        type="number"
                        unit={inputFields.pressureUnit.translated}
                        value={inputFields?.pressureDropExhaust}
                        onChange={(val) =>
                            onInputFieldChangeHandler(
                                'pressureDropExhaust',
                                val
                            )
                        }
                        step={pressureDropStep}
                        disabled={extractDisabled}
                    />
                </InputFieldContainer>
                <EmptyGridRow $gridrow={7} $gridcolumn={1} />
                <InputFieldLabel
                    $gridrow={8}
                    $gridcolumn={2}
                    htmlFor="climate-country"
                >
                    {l('climate.country')}
                </InputFieldLabel>
                <InputFieldLabel
                    $gridrow={8}
                    $gridcolumn={3}
                    htmlFor="climate-city"
                >
                    {l('climate.city')}
                </InputFieldLabel>
                <InputFieldLabel $gridrow={9} $gridcolumn={1}>
                    {l('climate.climate')}
                    {/*TODO Replace native title with tooltip lib later*/}
                    <IconContainer
                        title={l('ahuDesign.climateSelectionTooltip')}
                    >
                        <Info size="small" />
                    </IconContainer>
                </InputFieldLabel>
                <InputFieldContainer $gridrow={9} $gridcolumn={2}>
                    <InputField
                        id="climate-country"
                        type="select"
                        options={countries}
                        value={selectedCountryAndCity.selectedCountryKey}
                        onChange={(value) =>
                            onCountryChanged(Number.parseInt(value))
                        }
                    />
                </InputFieldContainer>
                <InputFieldContainer $gridrow={9} $gridcolumn={3}>
                    <InputField
                        id="climate-city"
                        type="select"
                        options={citiesToShow}
                        value={selectedCountryAndCity.selectedCityKey}
                        onChange={(value) => {
                            onCityChanged(Number.parseInt(value));
                        }}
                    />
                </InputFieldContainer>
                <EmptyGridRow $gridrow={10} $gridcolumn={1} />
                <InputFieldLabel $gridrow={11} $gridcolumn={1}>
                    {l('ahuDesignLite.coilAndDampers.title')}
                </InputFieldLabel>
                <InputFieldContainer
                    $gridrow={11}
                    $gridcolumn={2}
                    title={
                        !coilAndDamperOptions.coils
                            ? l('ahuDesignLite.coil.disabled')
                            : undefined
                    }
                >
                    <RadioButtons
                        group="coil"
                        disabled={!coilAndDamperOptions.coils}
                        items={coilsToShow}
                        selectedValue={inputFields.coil}
                        onChange={(value) =>
                            onInputFieldChangeHandler('coil', value)
                        }
                    ></RadioButtons>
                </InputFieldContainer>
                <InputFieldContainer $gridrow={11} $gridcolumn={3}>
                    {coilAndDamperOptions.dampers.map((damper, index) => {
                        const checked =
                            inputFields.dampers?.includes(damper) ?? false;
                        return (
                            <Checkbox
                                id={`damper-${index}`}
                                key={index}
                                label={l(damperTranslationMap[damper])}
                                checked={checked}
                                onChange={() => {
                                    onDampersChangedHandler(!checked, damper);
                                }}
                            />
                        );
                    })}
                </InputFieldContainer>
            </AirDataContainer>
        </div>
    );
};

export default InputFieldsContainer;
